import { gql } from '@apollo/client';
import * as yup from 'yup';
import { useRecord } from '../../utils/hooks';
import {
  UpsertCropTemplateMutation,
  UpsertFarmCropMutation,
} from '../../mutations';
import { nullableNumber } from '../../utils/yup';
import RevenueTableForm from './RevenueTableForm';
import {
  allPercentsValid,
  revenueStreamsSpecified,
} from '../../utils/revenueStreams';
import { CARBON_NAME } from '../../constants/revenueStreams';

const fields = [
  'id',
  'name',
  'percent',
  'unitCost',
  'unit',
  'lbsPerUnit',
  'unitsPerTree',
];

const RevenueFields = `
  id
  name
  updatedAt
  yield {
    id
    unit
  }
  revenueStreams {
    ${fields.join(' ')}
  }
`;

const TemplateRevenueQuery = gql`
  query TemplateRevenue($id: ID!) {
    cropTemplate(id: $id) {
      ${RevenueFields}
      team {
        id
      }
    }
  }
`;

const RawCropRevenueQuery = gql`
  query RawCropRevenue($id: ID!) {
    rawCrop(id: $id) {
      ${RevenueFields}
    }
  }
`;

const revenueStreamSchema = yup.object().shape({
  name: yup
    .string()
    .required('Required')
    .test({
      name: 'not-co2',
      message: 'CO2 revenue stream must be configured in the Carbon tab',
      test: (value) => value !== CARBON_NAME,
    }),
  unit: yup.string().required('Required'),
  lbsPerUnit: yup
    .number()
    .min(0, 'Minimum is 0')
    .when('unitsPerTree', {
      is: (unitsPerTree) => unitsPerTree == null,
      then: (schema) => schema.required('Required'),
      otherwise: (schema) => schema.nullable(),
    }),
  percent: yup
    .array()
    .of(nullableNumber().min(0, 'Minimum is 0').max(100, 'Must be 100 or less'))
    .required('Required'),
  unitCost: yup
    .array()
    .of(nullableNumber().min(0, 'Minimum is 0'))
    .required('Required'),
  unitsPerTree: yup
    .array()
    .of(nullableNumber().min(0, 'Minimum is 0'))
    .nullable(),
});

const revenueStreamsSchema = yup.object().shape({
  revenueStreams: yup
    .array()
    .of(revenueStreamSchema)
    .test({
      name: 'percents-valid',
      message: 'Percents must add to 100 each year',
      test: allPercentsValid,
    })
    .test({
      name: 'streams-specified',
      message: 'Specify missing values',
      test: revenueStreamsSpecified,
    }),
});

const transformValues = (values, { id, updatedAt, team }) => ({
  id,
  updatedAt,
  teamId: team?.id,
  revenueStreams: values.revenueStreams.map((stream) => ({
    ...stream,
    __typename: undefined,
    localId: undefined,
  })),
});

const CropRevenue = ({ onSubmit, msg, resource, status }) => {
  const { loading } = status;

  return (
    <RevenueTableForm
      data={resource}
      msg={msg}
      loading={loading}
      onSubmit={onSubmit(revenueStreamsSchema)}
      validationSchema={revenueStreamsSchema}
    />
  );
};

export const TemplateCropRevenue = () => {
  const props = useRecord({
    submitMutation: UpsertCropTemplateMutation,
    fetchRecordQuery: TemplateRevenueQuery,
    transformValues,
  });
  return <CropRevenue {...props} />;
};

export const FarmCropRevenue = () => {
  const props = useRecord({
    submitMutation: UpsertFarmCropMutation,
    fetchRecordQuery: RawCropRevenueQuery,
    transformValues,
    includeFarmId: true,
  });
  return <CropRevenue {...props} />;
};
